/* Copyright (c) 2026 Xperi. All Rights Reserved. */

// Imported from the concrete module rather than the `../logging` barrel: this
// runs on the earliest boot path, same reasoning as silentReloadGuard.ts.
import { Log } from "../logging/Log";

const TAG: string = "restartLimitGuard";

/**
 * Session-wide cap on silent restarts, on top of `silentReloadGuard`'s
 * per-failure cooldown.
 *
 * A single failure signature (a chunk, an unmatched route) only ever gets one
 * silent restart per 60s cooldown — but a device stuck in a bad state (a
 * corrupt cache, a broken network path) can hit a *different* signature each
 * time, and every one of those individually passes that per-signature guard.
 * This counts restarts across all signatures in the session and refuses once
 * {@link SILENT_RESTART_LIMIT} is reached, so the caller shows a real error
 * screen instead of reloading indefinitely.
 *
 * The count resets once the app has gone {@link SILENT_RESTART_RESET_DELAY_MS}
 * without another restart — a device that hit a rough patch and recovered
 * should not be penalized by an unrelated failure much later in the same
 * tab's lifetime.
 */

/** sessionStorage key holding the restart count and its most recent timestamp. */
export const SILENT_RESTART_STORAGE_KEY: string = "unity.silentRestartCount";

/** How many silent restarts are allowed before the caller must show an error screen. */
export const SILENT_RESTART_LIMIT: number = 5;

/** How long since the last restart before the count resets to zero. */
export const SILENT_RESTART_RESET_DELAY_MS: number = 5 * 60 * 1000;

interface ISilentRestartRecord {
  /** Silent restarts recorded since the count was last reset. */
  count: number;
  /** `Date.now()` at the most recent restart. */
  at: number;
}

/**
 * Outcome of reading the stored record. `isReadable` separates "no record yet"
 * from "this store cannot be read": the second must never allow a restart,
 * because a count we cannot read back is one we would repeat every time —
 * same distinction as `silentReloadGuard`'s read result.
 */
interface ISilentRestartReadResult {
  isReadable: boolean;
  record: ISilentRestartRecord | null;
}

/**
 * `window.sessionStorage`, or null when it is unreachable — an embedded TV
 * browser with storage disabled throws on property access, not just on use.
 */
function getSessionStorage(): Storage | null {
  try {
    const storage: Storage | undefined = window.sessionStorage;
    if (storage === null || storage === undefined) {
      return null;
    }
    return storage;
  } catch (error: unknown) {
    Log.w(TAG, "sessionStorage unavailable", error);
    return null;
  }
}

function readRecord(storage: Storage): ISilentRestartReadResult {
  let raw: string | null = null;
  try {
    raw = storage.getItem(SILENT_RESTART_STORAGE_KEY);
  } catch (error: unknown) {
    Log.w(TAG, "Failed to read restart count", error);
    return { isReadable: false, record: null };
  }

  if (raw === null || raw.length === 0) {
    return { isReadable: true, record: null };
  }

  try {
    const parsed = JSON.parse(raw) as { count?: unknown; at?: unknown };
    if (typeof parsed.count !== "number" || typeof parsed.at !== "number") {
      return { isReadable: true, record: null };
    }
    return {
      isReadable: true,
      record: { count: parsed.count, at: parsed.at }
    };
  } catch (error: unknown) {
    // A corrupt value is not an unusable store: the next write replaces it.
    Log.w(TAG, "Discarding corrupt restart count", error);
    return { isReadable: true, record: null };
  }
}

/** Whether `record` is still within its reset window. */
function isRecordActive(record: ISilentRestartRecord): boolean {
  return Date.now() - record.at < SILENT_RESTART_RESET_DELAY_MS;
}

/**
 * Whether the session has a silent restart left to spend. Read-only: safe to
 * call during a React render to decide what to paint.
 *
 * With no usable `sessionStorage` the answer is always no — same reasoning as
 * `silentReloadGuard.canAttemptSilentReload`: a count that cannot be
 * remembered is one that cannot be capped.
 */
export function canAttemptRestart(): boolean {
  const storage: Storage | null = getSessionStorage();
  if (storage === null) {
    return false;
  }
  const result: ISilentRestartReadResult = readRecord(storage);
  if (!result.isReadable) {
    return false;
  }
  const record: ISilentRestartRecord | null = result.record;
  if (record === null || !isRecordActive(record)) {
    return true;
  }
  return record.count < SILENT_RESTART_LIMIT;
}

/**
 * Claims one restart against the session's budget. Returns false when it
 * could not be recorded — the caller must then not navigate, the same
 * "an attempt that leaves no trace repeats forever" reasoning as
 * `silentReloadGuard.recordSilentReloadAttempt`.
 */
export function recordRestartAttempt(): boolean {
  const storage: Storage | null = getSessionStorage();
  if (storage === null) {
    return false;
  }
  const record: ISilentRestartRecord | null = readRecord(storage).record;
  const count: number =
    record !== null && isRecordActive(record) ? record.count + 1 : 1;
  try {
    storage.setItem(
      SILENT_RESTART_STORAGE_KEY,
      JSON.stringify({ count, at: Date.now() })
    );
    return true;
  } catch (error: unknown) {
    Log.w(TAG, "Failed to record restart attempt", error);
    return false;
  }
}
